/**
 * vingreact#1337 (Q-373, measure first): how often does the last save a
 * leaving /view page sends never reach the server?
 *
 * At page close `sendPagehideKeepalive` records, in this browser, WHICH
 * answer saves it sent on the way out (component id, save_seq, path), never
 * what they held. The next open of the same link for the same viewer compares
 * them with the GET's `answer_seq` echo and reports each save the server never
 * applied: one `consume_pagehide_save_lost` report (WARN on the server,
 * vingapi#1950; the count is a FLOOR, a lost save replaced by a later save of
 * the same component from another device counts as landed). Nothing on screen
 * changes. Every storage access is in try/catch: blocked or full storage
 * records nothing and never throws into the pagehide handler.
 */
import { reportClientError } from './clientErrors';
import { getClientId, getSessionId } from '@/lib/traceContext';

export const LEAVE_MARKER_KEY_PREFIX = 'ving.consume.leave.';
export const LEAVE_MARKER_VERSION = 1;
export const LEAVE_MARKER_MAX_SAVES = 50;
export const LEAVE_MARKER_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
/** `lost[]` never carries more than this many entries. */
export const LOST_REPORT_MAX_ENTRIES = 20;
/** vingapi's CLIENT_CRASH_CONTEXT_MAX is 2000 bytes serialized; stay under it. */
export const LOST_REPORT_CONTEXT_MAX_BYTES = 1800;

export const PAGEHIDE_SAVE_LOST_REPORT_NAME = 'ConsumePagehideSaveLost';
export const PAGEHIDE_SAVE_LOST_REPORT_KIND = 'consume_pagehide_save_lost';
const PAGEHIDE_SAVE_LOST_MESSAGE = 'A save sent when the page was closed never reached the server';

export type LeavePath = 'keepalive' | 'no_transport' | 'too_large';
const LEAVE_PATHS: ReadonlySet<string> = new Set<LeavePath>(['keepalive', 'no_transport', 'too_large']);

export interface LeaveSave {
  component_id: number;
  save_seq: number;
  path: LeavePath;
}

interface LeaveMarker {
  v: typeof LEAVE_MARKER_VERSION;
  viewer: number | null;
  at: number;
  saves: LeaveSave[];
  truncated?: true;
}

const markerKey = (stub: string): string => `${LEAVE_MARKER_KEY_PREFIX}${stub}`;

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** Overwrite this stub's marker with the saves the close just handled. Never throws. */
export function recordLeaveMarker(stub: string, viewer: number | null, saves: ReadonlyArray<LeaveSave>): void {
  if (saves.length === 0) return; // nothing handled this pass: keep what an earlier pass recorded
  try {
    const marker: LeaveMarker = {
      v: LEAVE_MARKER_VERSION,
      viewer,
      at: Date.now(),
      saves: saves.slice(0, LEAVE_MARKER_MAX_SAVES),
      ...(saves.length > LEAVE_MARKER_MAX_SAVES ? { truncated: true as const } : {}),
    };
    storage()?.setItem(markerKey(stub), JSON.stringify(marker));
  } catch {
    // Blocked or full storage: record nothing; the send goes out as today.
  }
}

function isSave(value: unknown): value is LeaveSave {
  if (typeof value !== 'object' || value === null) return false;
  const s = value as Record<string, unknown>;
  return (
    typeof s.component_id === 'number' &&
    Number.isSafeInteger(s.component_id) &&
    typeof s.save_seq === 'number' &&
    Number.isSafeInteger(s.save_seq) &&
    typeof s.path === 'string' &&
    LEAVE_PATHS.has(s.path)
  );
}

function parseMarker(raw: string): LeaveMarker | null {
  try {
    const m: unknown = JSON.parse(raw);
    if (typeof m !== 'object' || m === null) return null;
    const o = m as Record<string, unknown>;
    if (o.v !== LEAVE_MARKER_VERSION || typeof o.at !== 'number' || !Array.isArray(o.saves)) return null;
    if (o.viewer !== null && typeof o.viewer !== 'number') return null;
    if (!o.saves.every(isSave)) return null;
    return { v: LEAVE_MARKER_VERSION, viewer: o.viewer, at: o.at, saves: o.saves, ...(o.truncated === true ? { truncated: true } : {}) };
  } catch {
    return null;
  }
}

function utf8Length(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** The context `reportClientError` will serialize: it adds `client_id`, `session_id` (#1104) and `user_agent` (256 chars max). */
function sentContextBytes(context: Record<string, unknown>): number {
  const userAgent = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent.slice(0, 256) : undefined;
  return utf8Length(JSON.stringify({ client_id: getClientId(), session_id: getSessionId(), ...context, user_agent: userAgent }));
}

/**
 * The first successful GET of `stub` in this page: read and REMOVE the marker
 * (so it reports once), skip it when stale, another viewer's, or the response
 * has no `answer_seq`, else report the saves the server never applied.
 * `serverSeq >= save_seq` is landed. Never throws.
 */
export function checkLeaveMarker(
  stub: string,
  viewer: number | null,
  answerSeq: Record<string, number | null> | null | undefined,
): void {
  let marker: LeaveMarker | null = null;
  try {
    const store = storage();
    const raw = store?.getItem(markerKey(stub));
    if (store) store.removeItem(markerKey(stub));
    if (raw == null) return;
    marker = parseMarker(raw);
  } catch {
    return;
  }
  if (!marker || marker.saves.length === 0) return;
  const age = Date.now() - marker.at;
  if (age > LEAVE_MARKER_MAX_AGE_MS) return;
  if (marker.viewer !== viewer) return;
  if (!answerSeq) return;

  const lost = marker.saves
    .map((s) => ({ ...s, server_seq: answerSeq[String(s.component_id)] ?? null }))
    .filter((s) => s.server_seq === null || s.server_seq < s.save_seq);
  if (lost.length === 0) return;

  const context = (entries: typeof lost): Record<string, unknown> => ({
    share_stub: stub,
    surface: 'packet',
    lost: entries,
    lost_count: lost.length,
    recorded: marker.saves.length,
    age_ms: age,
    truncated: marker.truncated === true || entries.length < lost.length,
  });
  let kept = lost.slice(0, LOST_REPORT_MAX_ENTRIES);
  while (kept.length > 1 && sentContextBytes(context(kept)) > LOST_REPORT_CONTEXT_MAX_BYTES) {
    kept = kept.slice(0, -1);
  }
  reportClientError({
    error: PAGEHIDE_SAVE_LOST_MESSAGE,
    name: PAGEHIDE_SAVE_LOST_REPORT_NAME,
    source: 'consume',
    kind: PAGEHIDE_SAVE_LOST_REPORT_KIND,
    context: context(kept),
  });
}
